import { FC, useContext } from "react";

import {
  Analytics,
  AssessmentOutlined,
  CompareArrows,
  Description,
  Edit,
  FindInPage,
  MailOutline,
  Security,
  Settings,
} from "@mui/icons-material";
import {
  IconButton,
  Theme,
  Tooltip,
  useMediaQuery,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { NavLink } from "react-router-dom";

import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { IUserPermission } from "@/types/typesAlerts";

import { collapsedAppIconSx } from "./collapsedAppIconSx";
import { frameNavItemSx } from "./frameNavItemSx";

interface Props {
  dense: boolean;
  logout: () => void;
  onMenuClick: () => void;
}

const RubiIntelMenu: FC<Props> = ({ onMenuClick, dense, logout }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery((theme as Theme).breakpoints.down("sm"));
  const { collapsed, setCollapsed } = useSidebar();
  const { currentUser, getOrgIDFromURL } = useContext(AuthStateContext);
  const orgID = getOrgIDFromURL() ?? currentUser?.rubi_org_id ?? "";
  const { isDevEnv } = useDevEnvironment();
  const hideSideBar = () => {
    setCollapsed(true);
  };

  const reportPermissions =
    currentUser &&
    parseUserPermissions(currentUser?.permissions as IUserPermission[]);

  const menuItems = [
    {
      to: `/rubiintel/${orgID}/reports`,
      text: "Reports",
      icon: <AssessmentOutlined />,
      visible: !reportPermissions || reportPermissions.canViewEntriesIntel,
    },
    {
      to: `/rubiintel/${orgID}/ocis`,
      text: "CISO",
      icon: <Security />,
      visible: true, // OCIS always works
    },
    {
      to: `/rubiintel/${orgID}/drafts`,
      text: "Drafts",
      icon: <Edit />,
      visible: true,
    },
    {
      to: `/rubiintel/${orgID}/match-reports`,
      text: "Match Reports",
      icon: <CompareArrows />,
      visible: !reportPermissions || reportPermissions.canViewEntriesIntel,
    },
    {
      to: `/rubiintel/${orgID}/dashboard`,
      text: "Dashboard",
      icon: <Analytics />,
      visible: !reportPermissions || reportPermissions.canViewDashboardIntel,
    },
    {
      to: `/rubiintel/${orgID}/settings`,
      text: "Settings",
      icon: <Settings />,
      visible: !reportPermissions || reportPermissions.canViewSettingsIntel,
    },
    {
      to: `/rubiintel/${orgID}/request-submission`,
      text: "Request",
      icon: <MailOutline />,
      visible: !reportPermissions || reportPermissions.canViewEntriesIntel,
    },
  ].filter((item) => item.visible);

  const devMenuItems = [
    {
      to: `/rubiintel/${orgID}/forms`,
      text: "Forms",
      icon: <Description />,
      visible: !reportPermissions || reportPermissions.canViewEntriesIntel,
    },
  ];

  return (
    <div className="menu">
      {!collapsed ? (
        <div className="sidebar-top">
          <div className="app-name">
            Rubi<span>INTEL</span>
          </div>
        </div>
      ) : (
        <div className="application-item">
          <Tooltip
            title="Intelligence Management System"
            placement="right"
            arrow
          >
            <IconButton
              aria-label="RubiINTEL"
              size="large"
              disableRipple
              sx={collapsedAppIconSx}
            >
              <FindInPage />
            </IconButton>
          </Tooltip>
        </div>
      )}
      <div className="left-menu">
        {menuItems.map((item) => (
          <MenuItem
            key={item.to}
            component={NavLink}
            to={item.to}
            onClick={isSmall ? hideSideBar : undefined}
            sx={[
              frameNavItemSx(),
              {
                pl: 2,
                py: dense ? 1 : 1.5,
              },
            ]}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            {!collapsed && <ListItemText primary={item.text} />}
          </MenuItem>
        ))}
        {isDevEnv &&
          devMenuItems.map((item) => (
            <MenuItem
              key={item.to}
              component={NavLink}
              to={item.to}
              onClick={isSmall ? hideSideBar : undefined}
              sx={[
                frameNavItemSx(),
                {
                  pl: 2,
                  py: dense ? 1 : 1.5,
                },
              ]}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              {!collapsed && <ListItemText primary={item.text} />}
            </MenuItem>
          ))}
        {/* //TODO Enable once we have a dashboards */}
        {/* <DashboardMenuItem sidebarIsOpen={!collapsed} /> */}
      </div>
    </div>
  );
};

export default RubiIntelMenu;
